import React, { useMemo } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { NumberInput } from './ui/number-input';
import { DatePicker } from './ui/date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import BankCombobox from './BankCombobox';
import { Tip } from './ui/tip';
import { formatCurrencyAR } from '../utils/currencyFormatter';
import {
  LineaPago, METODO_MIXTO, completarResto, lineaVacia, repartirLineas
} from '../utils/cobroMixto';

/**
 * Editor de cobro mixto: un renglón por medio de pago con su parte del total.
 * El recargo (tarjetas) se calcula SOLO sobre la parte de ese renglón y se
 * muestra en "Cobra". Se usa en Remitos (alta/edición, cambio de medio,
 * emitir sin medio) y en Taller (retiro del auto).
 */
export const PagoMixtoEditor: React.FC<{
  /** Total del remito con descuento y sin recargo: lo que se reparte */
  base: number;
  lineas: LineaPago[];
  onChange: (lineas: LineaPago[]) => void;
  opciones: Array<{ value: string; label: string; surcharge?: number }>;
  cuentas?: Array<{ id: string; name: string; kind?: string }>;
  ccIntervals?: Array<{ value: number; label: string }>;
  className?: string;
}> = ({ base, lineas, onChange, opciones, cuentas = [], ccIntervals = [], className }) => {
  const opcionesLineas = useMemo(() => opciones.filter((o) => o.value !== METODO_MIXTO), [opciones]);
  const recargos = useMemo(
    () => Object.fromEntries(opcionesLineas.map((o) => [o.value, Number(o.surcharge) || 0])),
    [opcionesLineas]
  );
  const reparto = repartirLineas(base, lineas, recargos);
  const cuentasBanco = cuentas.filter((a) => a.kind !== 'efectivo');

  const setLinea = (i: number, patch: Partial<LineaPago>) =>
    onChange(lineas.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const quitar = (i: number) => onChange(lineas.filter((_, idx) => idx !== i));
  const agregar = () => onChange([...lineas, lineaVacia()]);

  return (
    <div className={`rounded-lg border border-dashed p-3 space-y-3 ${className || ''}`}>
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          Repartí el total entre los medios. El recargo de tarjeta se aplica solo a la parte que va con tarjeta.
        </p>
        <span className="text-xs font-medium whitespace-nowrap">Total {formatCurrencyAR(base)}</span>
      </div>

      <div className="space-y-2">
        {lineas.map((l, i) => {
          const calc = reparto.lineas[i];
          const puedeCompletar = reparto.faltante > 0.5 && !(l.base > 0);
          return (
            <div key={l.key} className="rounded-md border bg-background p-2 space-y-2">
              <div className="grid grid-cols-[minmax(0,1fr)_112px_32px] items-center gap-2">
                <Select value={l.method || undefined} onValueChange={(v) => setLinea(i, { method: v || '' })}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Medio de pago" /></SelectTrigger>
                  <SelectContent>
                    {opcionesLineas.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}{Number(o.surcharge) > 0 ? ` (+${Number(o.surcharge)}%)` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <NumberInput
                  value={l.base}
                  onChange={(v) => setLinea(i, { base: v })}
                  className="h-9 text-right tabular-nums"
                  placeholder="Importe"
                  inputMode="decimal"
                />
                <Tip label="Quitar">
                  <button
                    type="button"
                    onClick={() => quitar(i)}
                    disabled={lineas.length <= 2}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-destructive disabled:opacity-40"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </Tip>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs">
                <span className="text-muted-foreground">
                  {calc && calc.surcharge > 0
                    ? `Cobra ${formatCurrencyAR(calc.amount)} (${formatCurrencyAR(calc.base)} + ${calc.surchargePct}%)`
                    : calc && calc.base > 0 ? `Cobra ${formatCurrencyAR(calc.amount)}` : ''}
                </span>
                {puedeCompletar && (
                  <button
                    type="button"
                    className="font-medium text-primary underline-offset-2 hover:underline"
                    onClick={() => onChange(completarResto(lineas, base, i))}
                  >
                    Usar el resto ({formatCurrencyAR(reparto.faltante)})
                  </button>
                )}
              </div>

              {['transferencia', 'debito', 'qr'].includes(l.method) && cuentasBanco.length > 0 && (
                <div>
                  <Label className="text-xs">Cuenta que recibe</Label>
                  <Select value={l.accountId || undefined} onValueChange={(v) => setLinea(i, { accountId: v || '' })}>
                    <SelectTrigger className="h-8"><SelectValue placeholder="¿A qué cuenta entró?" /></SelectTrigger>
                    <SelectContent>
                      {cuentasBanco.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {l.method === 'cuenta_corriente' && ccIntervals.length > 0 && (
                <div>
                  <Label className="text-xs">Plazo</Label>
                  <Select value={l.paymentInterval || undefined} onValueChange={(v) => setLinea(i, { paymentInterval: v || '' })}>
                    <SelectTrigger className="h-8"><SelectValue placeholder="Plazo de pago" /></SelectTrigger>
                    <SelectContent>
                      {ccIntervals.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {l.method === 'cheque' && (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <div>
                    <Label className="text-xs">N° de cheque</Label>
                    <Input className="h-8" value={l.chequeNumero || ''} onChange={(e) => setLinea(i, { chequeNumero: e.target.value })} placeholder="12345678" />
                  </div>
                  <div>
                    <Label className="text-xs">Banco</Label>
                    <BankCombobox value={l.chequeBanco || ''} onChange={(v) => setLinea(i, { chequeBanco: v })} className="h-8" />
                  </div>
                  <div>
                    <Label className="text-xs">Vencimiento</Label>
                    <DatePicker value={l.chequeVencimiento || ''} onChange={(v) => setLinea(i, { chequeVencimiento: v })} className="h-8" />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button type="button" variant="outline" size="sm" className="h-8" onClick={agregar}>
          <PlusIcon className="mr-1 h-4 w-4" />Agregar medio
        </Button>
        <div className="text-right text-xs">
          {Math.abs(reparto.faltante) > 0.5 ? (
            <span className={reparto.faltante > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-destructive'}>
              {reparto.faltante > 0 ? `Faltan ${formatCurrencyAR(reparto.faltante)}` : `Sobran ${formatCurrencyAR(-reparto.faltante)}`}
            </span>
          ) : (
            <span className="text-muted-foreground">Cierra con el total</span>
          )}
          {reparto.recargoTotal > 0 && (
            <span className="ml-2 text-muted-foreground">· Recargo {formatCurrencyAR(reparto.recargoTotal)}</span>
          )}
          <span className="ml-2 font-semibold text-foreground">Cobra {formatCurrencyAR(reparto.total)}</span>
        </div>
      </div>
      {!reparto.ok && reparto.error && !reparto.error.startsWith('Los importes') && (
        <p className="text-xs text-muted-foreground">{reparto.error}</p>
      )}
    </div>
  );
};

export default PagoMixtoEditor;
